import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExt from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import ImageExt from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { ArrowLeft, Save, Plus, Trash2, ExternalLink, Play, FileText, ArrowUp, ArrowDown, Upload, History, RotateCcw, Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2, Heading3, List, ListOrdered, Quote, Code, Link2, Image, Minus, ImagePlus, Loader2 } from 'lucide-react';

function EditorToolbar({ editor }) {
  const imageInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  if (!editor) return null;

  const btn = (action, active, icon, label, disabled = false) => (
    <button type="button" onClick={action} title={label} disabled={disabled}
      className={`p-1.5 rounded transition-colors ${disabled ? 'opacity-40 cursor-not-allowed' : ''} ${active ? 'bg-[#F5C518]/20 text-[#D4A910]' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
    >{icon}</button>
  );
  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/upload', formData);
      if (res.data?.url) {
        editor.chain().focus().setImage({ src: res.data.url, alt: file.name }).run();
      }
    } catch (err) {
      console.error('Image upload failed:', err);
      alert('Image upload failed. Please try again.');
    }
    setUploading(false);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };
  const addImageByUrl = () => {
    const url = window.prompt('Enter image URL:');
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <div className="flex flex-wrap gap-0.5 p-2 border-b border-gray-200 bg-gray-50">
      {btn(() => editor.chain().focus().toggleBold().run(), editor.isActive('bold'), <Bold className="w-4 h-4" />, 'Bold')}
      {btn(() => editor.chain().focus().toggleItalic().run(), editor.isActive('italic'), <Italic className="w-4 h-4" />, 'Italic')}
      {btn(() => editor.chain().focus().toggleUnderline().run(), editor.isActive('underline'), <UnderlineIcon className="w-4 h-4" />, 'Underline')}
      <div className="w-px h-6 bg-gray-200 mx-1 self-center" />
      {btn(() => editor.chain().focus().toggleHeading({ level: 1 }).run(), editor.isActive('heading', { level: 1 }), <Heading1 className="w-4 h-4" />, 'H1')}
      {btn(() => editor.chain().focus().toggleHeading({ level: 2 }).run(), editor.isActive('heading', { level: 2 }), <Heading2 className="w-4 h-4" />, 'H2')}
      {btn(() => editor.chain().focus().toggleHeading({ level: 3 }).run(), editor.isActive('heading', { level: 3 }), <Heading3 className="w-4 h-4" />, 'H3')}
      <div className="w-px h-6 bg-gray-200 mx-1 self-center" />
      {btn(() => editor.chain().focus().toggleBulletList().run(), editor.isActive('bulletList'), <List className="w-4 h-4" />, 'Bullet List')}
      {btn(() => editor.chain().focus().toggleOrderedList().run(), editor.isActive('orderedList'), <ListOrdered className="w-4 h-4" />, 'Ordered List')}
      {btn(() => editor.chain().focus().toggleBlockquote().run(), editor.isActive('blockquote'), <Quote className="w-4 h-4" />, 'Quote')}
      {btn(() => editor.chain().focus().toggleCodeBlock().run(), editor.isActive('codeBlock'), <Code className="w-4 h-4" />, 'Code')}
      <div className="w-px h-6 bg-gray-200 mx-1 self-center" />
      {btn(addLink, editor.isActive('link'), <Link2 className="w-4 h-4" />, 'Link')}
      {/* Image upload button */}
      <label title="Upload Image" className={`p-1.5 rounded transition-colors cursor-pointer ${uploading ? 'opacity-40' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}>
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
        <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
      </label>
      {btn(addImageByUrl, false, <Image className="w-4 h-4" />, 'Image URL')}
      {btn(() => editor.chain().focus().setHorizontalRule().run(), false, <Minus className="w-4 h-4" />, 'Rule')}
    </div>
  );
}

function ContentItemForm({ item, onSave, onDelete, onMove, isFirst, isLast }) {
  const [data, setData] = useState(item);
  const typeIcons = { loom_video: <Play className="w-4 h-4 text-red-500" />, hyperlink: <ExternalLink className="w-4 h-4 text-[#D4A910]" />, document: <FileText className="w-4 h-4 text-gray-500" /> };
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-testid={`content-item-${item.id}`}>
      <div className="flex items-center gap-2 mb-3">
        {typeIcons[data.item_type] || null}
        <span className="text-xs font-medium text-gray-400 uppercase">{data.item_type?.replace('_', ' ')}</span>
        <div className="ml-auto flex items-center gap-1">
          {!isFirst && <button onClick={() => onMove('up')} className="p-1 hover:bg-white rounded"><ArrowUp className="w-3 h-3 text-gray-400" /></button>}
          {!isLast && <button onClick={() => onMove('down')} className="p-1 hover:bg-white rounded"><ArrowDown className="w-3 h-3 text-gray-400" /></button>}
          <button onClick={onDelete} className="p-1 hover:bg-red-50 rounded"><Trash2 className="w-3 h-3 text-red-400" /></button>
        </div>
      </div>
      <div className="space-y-2">
        <input value={data.title || ''} onChange={e => setData({ ...data, title: e.target.value })} placeholder="Title" className="w-full px-3 py-2 bg-white border border-gray-200 rounded text-sm text-gray-900 placeholder:text-gray-300 focus:outline-none focus:border-[#F5C518]" />
        {(data.item_type === 'loom_video' || data.item_type === 'hyperlink') && (
          <input value={data.url || ''} onChange={e => setData({ ...data, url: e.target.value })} placeholder="URL" className="w-full px-3 py-2 bg-white border border-gray-200 rounded text-sm text-gray-900 placeholder:text-gray-300 focus:outline-none focus:border-[#F5C518]" />
        )}
        {data.item_type === 'hyperlink' && (
          <input value={data.description || ''} onChange={e => setData({ ...data, description: e.target.value })} placeholder="Description (optional)" className="w-full px-3 py-2 bg-white border border-gray-200 rounded text-sm text-gray-900 placeholder:text-gray-300 focus:outline-none focus:border-[#F5C518]" />
        )}
        {data.item_type === 'document' && (
          <input value={data.file_path || ''} onChange={e => setData({ ...data, file_path: e.target.value })} placeholder="File URL (upload first)" className="w-full px-3 py-2 bg-white border border-gray-200 rounded text-sm text-gray-900 placeholder:text-gray-300 focus:outline-none focus:border-[#F5C518]" />
        )}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
            <input type="checkbox" checked={data.visible !== false} onChange={e => setData({ ...data, visible: e.target.checked })} className="rounded border-gray-300" />
            Visible
          </label>
          <button onClick={() => onSave(data)} className="px-3 py-1 text-xs font-medium text-[#D4A910] bg-[#F5C518]/10 hover:bg-[#F5C518]/20 rounded transition-colors">Save Item</button>
        </div>
      </div>
    </div>
  );
}

function ResourceList({ onSelect }) {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get('/admin/resources').then(r => setChapters(r.data)).catch(console.error).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#F5C518] border-t-transparent rounded-full animate-spin" /></div>;
  return (
    <div className="space-y-4 animate-fade-in" data-testid="admin-content-list">
      <h2 className="text-xl font-bold text-gray-900">Content Management</h2>
      {chapters.map(({ chapter, resources }) => (
        <div key={chapter.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
            <span className="w-7 h-7 rounded bg-[#F5C518]/10 flex items-center justify-center text-xs font-bold text-[#D4A910]">{chapter.number}</span>
            <span className="text-sm font-semibold text-gray-900">{chapter.name}</span>
            <span className="text-xs text-gray-400 ml-auto">{resources.length} resources</span>
          </div>
          <div className="divide-y divide-gray-100">
            {resources.map(r => (
              <button key={r.id} onClick={() => onSelect(r.id)} className="w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors text-left" data-testid={`edit-resource-${r.url_path}`}>
                <code className="text-xs font-mono text-gray-400 shrink-0">{r.url_path}</code>
                <span className="text-sm text-gray-900 flex-1 truncate">{r.title}</span>
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${r.status === 'published' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>{r.status}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ResourceEditor({ resourceId, onBack }) {
  const [resource, setResource] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showRevisions, setShowRevisions] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [title, setTitle] = useState('');
  const [shortDesc, setShortDesc] = useState('');
  const [tags, setTags] = useState('');
  const [status, setStatus] = useState('published');
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [StarterKit, LinkExt.configure({ openOnClick: false }), Underline, ImageExt, Placeholder.configure({ placeholder: 'Write page content here...' })],
    content: '',
    editorProps: { attributes: { class: 'prose-content min-h-[200px] outline-none' } },
  });

  const fetchResource = useCallback(async () => {
    try {
      const res = await api.get(`/admin/resources/${resourceId}`);
      setResource(res.data);
      setItems(res.data.content_items || []);
      setTitle(res.data.title || '');
      setShortDesc(res.data.short_description || '');
      setTags(res.data.tags?.join(', ') || '');
      setStatus(res.data.status || 'published');
      if (editor) editor.commands.setContent(res.data.body_content || '');
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [resourceId, editor]);

  useEffect(() => { if (editor) fetchResource(); }, [fetchResource, editor]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put(`/admin/resources/${resourceId}`, {
        title, short_description: shortDesc,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        body_content: editor?.getHTML() || '',
        status,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleAddItem = async (type) => {
    try {
      const res = await api.post(`/admin/resources/${resourceId}/items`, { item_type: type, title: `New ${type.replace('_', ' ')}`, sort_order: items.length });
      setItems([...items, res.data]);
    } catch (err) { console.error(err); }
  };

  const handleSaveItem = async (data) => {
    try { await api.put(`/admin/items/${data.id}`, data); } catch (err) { console.error(err); }
  };

  const handleDeleteItem = async (id) => {
    try { await api.delete(`/admin/items/${id}`); setItems(items.filter(i => i.id !== id)); } catch (err) { console.error(err); }
  };

  const handleMoveItem = (index, direction) => {
    const newItems = [...items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    setItems(newItems);
    newItems.forEach((item, i) => api.put(`/admin/items/${item.id}`, { sort_order: i }).catch(console.error));
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/upload', formData);
      const newItem = await api.post(`/admin/resources/${resourceId}/items`, {
        item_type: 'document', title: file.name, file_path: res.data.url, file_size: res.data.file_size, sort_order: items.length,
      });
      setItems([...items, newItem.data]);
    } catch (err) { console.error(err); }
    setUploading(false);
  };

  const loadRevisions = async () => {
    try { const res = await api.get(`/admin/resources/${resourceId}/revisions`); setRevisions(res.data); setShowRevisions(true); } catch (err) { console.error(err); }
  };

  const restoreRevision = async (revId) => {
    try { await api.post(`/admin/resources/${resourceId}/restore/${revId}`); await fetchResource(); setShowRevisions(false); } catch (err) { console.error(err); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#F5C518] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="resource-editor">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" data-testid="editor-back"><ArrowLeft className="w-5 h-5 text-gray-400" /></button>
          <div>
            <code className="text-xs font-mono text-gray-400">/uai/{resource?.url_path}</code>
            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadRevisions} className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg transition-colors flex items-center gap-1" data-testid="revisions-button"><History className="w-3.5 h-3.5" />Revisions</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 text-xs font-medium text-black bg-[#F5C518] hover:bg-[#D4A910] rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50" data-testid="save-resource-button">
            <Save className="w-3.5 h-3.5" />{saving ? 'Saving...' : saved ? 'Saved!' : 'Save'}
          </button>
        </div>
      </div>

      {/* Revisions Modal */}
      {showRevisions && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowRevisions(false)}>
          <div className="bg-white border border-gray-200 rounded-xl p-6 w-full max-w-lg max-h-[70vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="revisions-modal">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Revision History</h3>
            {revisions.length === 0 ? <p className="text-xs text-gray-400">No revisions yet</p> : (
              <div className="space-y-2">
                {revisions.map(rev => (
                  <div key={rev.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-xs text-gray-700">{rev.title || 'Revision'}</p>
                      <p className="text-[10px] text-gray-400">{new Date(rev.created_at).toLocaleString()}</p>
                    </div>
                    <button onClick={() => restoreRevision(rev.id)} className="px-2 py-1 text-[10px] font-medium text-[#D4A910] bg-[#F5C518]/10 hover:bg-[#F5C518]/20 rounded transition-colors flex items-center gap-1" data-testid={`restore-revision-${rev.id}`}>
                      <RotateCcw className="w-3 h-3" />Restore
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main editor */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50"><h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Page Body</h3></div>
            <div className="tiptap-editor-light">
              <EditorToolbar editor={editor} />
              <EditorContent editor={editor} className="min-h-[300px] p-4" />
            </div>
          </div>

          {/* Content Items */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Content Items</h3>
              <div className="flex gap-1">
                <button onClick={() => handleAddItem('loom_video')} className="px-2 py-1 text-[10px] font-medium text-red-500 bg-red-50 hover:bg-red-100 rounded transition-colors flex items-center gap-1"><Play className="w-3 h-3" />Loom</button>
                <button onClick={() => handleAddItem('hyperlink')} className="px-2 py-1 text-[10px] font-medium text-[#D4A910] bg-[#F5C518]/10 hover:bg-[#F5C518]/20 rounded transition-colors flex items-center gap-1"><ExternalLink className="w-3 h-3" />Link</button>
                <label className="px-2 py-1 text-[10px] font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors flex items-center gap-1 cursor-pointer">
                  <Upload className="w-3 h-3" />{uploading ? 'Uploading...' : 'Upload'}
                  <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
                </label>
              </div>
            </div>
            <div className="space-y-3">
              {items.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No content items. Add Loom videos, links, or upload documents above.</p>}
              {items.map((item, idx) => (
                <ContentItemForm key={item.id} item={item} onSave={handleSaveItem} onDelete={() => handleDeleteItem(item.id)} onMove={(dir) => handleMoveItem(idx, dir)} isFirst={idx === 0} isLast={idx === items.length - 1} />
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Settings</h3>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Title</label>
              <input value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-200 rounded text-sm text-gray-900 focus:outline-none focus:border-[#F5C518]" data-testid="editor-title-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Short Description</label>
              <textarea value={shortDesc} onChange={e => setShortDesc(e.target.value)} rows={3} className="w-full px-3 py-2 bg-white border border-gray-200 rounded text-sm text-gray-900 focus:outline-none focus:border-[#F5C518] resize-none" data-testid="editor-description-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Tags (comma separated)</label>
              <input value={tags} onChange={e => setTags(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-200 rounded text-sm text-gray-900 focus:outline-none focus:border-[#F5C518]" data-testid="editor-tags-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-200 rounded text-sm text-gray-900 focus:outline-none focus:border-[#F5C518]" data-testid="editor-status-select">
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">URL Path</h3>
            <code className="text-sm text-[#D4A910]">/uai/{resource?.url_path}</code>
            <p className="text-[10px] text-gray-400 mt-1">Read-only. Matches book cross-references.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminEditor() {
  const { resourceId } = useParams();
  const navigate = useNavigate();

  if (resourceId) return <ResourceEditor resourceId={resourceId} onBack={() => navigate('/admin/content')} />;
  return <ResourceList onSelect={(id) => navigate(`/admin/content/${id}`)} />;
}
